import { CiroButtonStyleEnum } from "../shared/CiroButton";
import classNames from "classnames";
import CiroCard from "../shared/CiroCard";
import { SparklesIcon } from "@heroicons/react/20/solid";
import CiroButton from "../shared/CiroButton";
import { ApolloError, gql } from "@apollo/client";
import CoinIcon from "../../assets/img/icons/CoinIcon";
import {
  AutopilotPreview_AutopilotSessionPreviewFragment,
  AutopilotPreviewTable_ContactMatchPreviewFragmentDoc,
} from "../../__generated__/graphql";
import { useState } from "react";
import { useFragment } from "../../__generated__";
import AutopilotPreviewRunAutopilotModal from "./AutopilotPreviewRunAutopilotModal";
import AutopilotPreviewTable, {
  AutopilotPreviewTable_ContactMatchPreview,
} from "./AutopilotPreviewTable";
import CiroPill from "../shared/CiroPill";
import { FireIcon } from "@heroicons/react/24/outline";
import approx from "approximate-number";
import CiroAlert from "../shared/CiroAlert";

export const AutopilotPreview_AutopilotSessionPreview = gql`
  fragment AutopilotPreview_AutopilotSessionPreview on AutopilotSessionPreviewResponse {
    totalContacts
    multisearchDetail {
      contactCountFromPreviewCompanies
      numPreviewCompanies
      numTotalCompanies
    }
    contacts {
      org_contact {
        id
      }
    }
    ...AutopilotPreviewTable_ContactMatchPreview
  }
  ${AutopilotPreviewTable_ContactMatchPreview}
`;

export const enum AutopilotPreviewLoadingState {
  FetchingContacts = "fetchingContacts",
  RotatingContacts = "rotatingContacts",
  Error = "error",
  Success = "success",
  CompletingProgress = "completingProgress",
  ShowResults = "showResults",
}

const AutopilotPreview = ({
  autopilotSessionPreview,
  autopilotSessionPreviewError,
  curatedSearchId,
}: {
  autopilotSessionPreview: AutopilotPreview_AutopilotSessionPreviewFragment;
  autopilotSessionPreviewError?: ApolloError | null;
  curatedSearchId: number | null;
}) => {
  const [isRunAutopilotModalOpen, setIsRunAutopilotModalOpen] = useState(false);

  const contactsMatchPreviewTable = useFragment(
    AutopilotPreviewTable_ContactMatchPreviewFragmentDoc,
    autopilotSessionPreview,
  );

  if (!curatedSearchId) {
    return null;
  }

  return (
    <div className={classNames("w-full")}>
      {autopilotSessionPreviewError && (
        <CiroAlert
          message={
            "An error occurred while loading your search preview. Try hitting refresh or contacting support at help@ciro.io."
          }
          level="error"
        />
      )}

      <div
        className={classNames(
          "transition-opacity",
          "duration-500",
          "ease-in-out",
        )}
      >
        <div className={classNames("w-full", "pb-4")}>
          <div className={classNames("h-12", "flex", "items-center", "gap-2")}>
            <div>
              <SparklesIcon className={classNames("text-orange-500", "h-6")} />
            </div>
            <div className={classNames("text-md", "font-semibold")}>
              Preview results
            </div>
            {Boolean(autopilotSessionPreview?.totalContacts) && (
              <CiroPill background="bg-orange-100" fontColor="text-orange-500">
                <div className={classNames("flex", "items-center", "gap-1")}>
                  <FireIcon className={classNames("h-3")} />
                  <div>
                    {autopilotSessionPreview?.multisearchDetail ? (
                      <>
                        {autopilotSessionPreview?.multisearchDetail
                          ?.contactCountFromPreviewCompanies &&
                          autopilotSessionPreview?.multisearchDetail
                            ?.contactCountFromPreviewCompanies > 1000 &&
                          "+"}
                        {approx(
                          autopilotSessionPreview?.multisearchDetail
                            ?.contactCountFromPreviewCompanies || 0,
                        )}{" "}
                        found from first{" "}
                        {
                          autopilotSessionPreview.multisearchDetail
                            ?.numPreviewCompanies
                        }{" "}
                        companies (
                        {
                          autopilotSessionPreview.multisearchDetail
                            ?.numTotalCompanies
                        }{" "}
                        companies total to be searched)
                      </>
                    ) : (
                      <>
                        {autopilotSessionPreview?.totalContacts &&
                          autopilotSessionPreview.totalContacts > 1000 &&
                          "+"}
                        {approx(autopilotSessionPreview?.totalContacts || 0)}{" "}
                        potential matches
                      </>
                    )}
                  </div>
                </div>
              </CiroPill>
            )}
          </div>
          {autopilotSessionPreview && (
            <div className={classNames("text-sm", "text-zinc-500")}>
              {autopilotSessionPreview.contacts.length > 0
                ? `Review the first ${autopilotSessionPreview.contacts.length} contacts based on your criteria.`
                : "No contacts found matching your search criteria."}
            </div>
          )}
        </div>
        <CiroCard size="small" customClassName={classNames("w-full")}>
          <div className={classNames("max-h-[60vh]", "overflow-auto")}>
            {autopilotSessionPreview &&
            autopilotSessionPreview.contacts.length === 0 ? (
              <div className={classNames("p-6", "text-center")}>
                <div className={classNames("text-lg", "font-medium", "mb-2")}>
                  No contacts found
                </div>
                <div className={classNames("text-zinc-500", "mb-2", "text-sm")}>
                  We couldn't find any contacts matching your search criteria.
                  Try adjusting your search parameters to broaden your search.
                </div>
              </div>
            ) : (
              <AutopilotPreviewTable
                contactsMatchPreview={contactsMatchPreviewTable}
              />
            )}
          </div>
          <div className={classNames("-mx-4", "pt-4")}>
            <div
              className={classNames(
                "w-full",
                "flex",
                "justify-end",
                "gap-2",
                "pt-4",
                "border-t",
                "border-zinc-200",
                "px-4",
              )}
            >
              <CiroButton
                disabled={
                  (autopilotSessionPreview?.contacts.length ?? 0) === 0 ||
                  Boolean(autopilotSessionPreviewError)
                }
                analyticsField="Start autopilot internal search"
                style={CiroButtonStyleEnum.LOUD}
                onClick={() => setIsRunAutopilotModalOpen(true)}
              >
                <div className={classNames("flex", "items-center", "gap-1")}>
                  <span>Search for more contacts</span>
                  <CoinIcon />
                </div>
              </CiroButton>
            </div>
          </div>
        </CiroCard>
      </div>
      <AutopilotPreviewRunAutopilotModal
        isOpen={isRunAutopilotModalOpen}
        onClose={() => setIsRunAutopilotModalOpen(false)}
        curatedSearchId={curatedSearchId}
      />
    </div>
  );
};

export default AutopilotPreview;
